import React, {useContext, useEffect, useState} from 'react';
import {
	Elements,
	useStripe,
	useElements,
	CardNumberElement
} from '@stripe/react-stripe-js';

import './StripeCardRegistration.less';
import {Button} from 'elements/button/Button'
import {CardDetails} from "compositions/card-details/CardDetails";
import {Error} from "elements/error/Error";
import {TranslationsContext} from "contexts/translations-context";
import {getSafeTranslation} from "helpers/localization.helper.ts";
import {DrawerPopup} from "dialog-layout/drawer-popup/DrawerPopup.tsx";
import {setInfoMessage, setJsError} from "../../../managers/client-log.manager";
import {ErrorBoundary} from "../../error-boundary/ErrorBoundary";

export const STRIPE_CARD_REGISTRATION_POPUP = 'STRIPE_CARD_REGISTRATION_POPUP';
export const PROFILE_STRIPE_CARD_REGISTRATION_POPUP = 'PROFILE_STRIPE_CARD_REGISTRATION_POPUP';

const CheckoutButtonWrapper = ({stripeClientSecret, isLoading, onSubmitStart, onSubmitDone}) => {
	const stripe = useStripe();
	const elements = useElements();

	const translations = useContext(TranslationsContext);

	const handleSubmit = async (event) => {
		event.preventDefault();

		onSubmitStart();

		const {error} = await stripe.confirmCardPayment(stripeClientSecret, {
			payment_method: {
				card: elements.getElement(CardNumberElement),
			}
		}, {
			save_payment_method: false
		});

		onSubmitDone(error);
	};

	return (<Button onClick={handleSubmit} isAutoWidth isDisabled={!stripe || isLoading}>
		{getSafeTranslation('StripeCardRegistrationPopup.doneButtonCaption', translations)}
	</Button>);
};

export const StripeCardRegistration = ({stripeKey, stripeClientSecret, onRegistrationSuccess, onRegistrationError, onCancelClick, onOverlayClick}) => {

	const [stripePromise, setStripePromise] = useState(null);
	const [errorText, setErrorText] = useState(null);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		if (!stripeKey) return;

		import('@stripe/stripe-js').then(({loadStripe}) => {
			setIsLoading(false);
			setStripePromise(loadStripe(stripeKey));
		});
	}, [stripeKey]);

	const translations = useContext(TranslationsContext);

	return (<Elements stripe={stripePromise}>
		<DrawerPopup
			title={getSafeTranslation('StripeCardRegistrationPopup.title', translations)}
			isLoading={isLoading}
			onOverlayClick={onOverlayClick}
			leftControl={
				<Button isText onClick={onCancelClick}>{
					getSafeTranslation('StripeCardRegistrationPopup.cancelButtonCaption', translations)
				}</Button>
			}
			rightControl={<CheckoutButtonWrapper
				stripeClientSecret={stripeClientSecret}
				isLoading={isLoading}
				onSubmitStart={() => setIsLoading(true)}
				onSubmitDone={error => {
					setIsLoading(false);
					if (error) {
						setErrorText(error.message);
						setInfoMessage('Stripe error: ' + error.message);
					} else {
						setInfoMessage('Stripe registration success');
						onRegistrationSuccess();
					}
				}}
			/>}
		>
			<ErrorBoundary
				onDidCatch={(error, errorInfo) => {
					setJsError(`StripeCardRegistration js error: ${error} ${errorInfo}`);
				}}
				renderError={errorMessage => {
					return <Error text={errorMessage}/>
				}}
			>
				<div className="stripe-card-registration">
					<div className="stripe-card-registration__card-details">
						<CardDetails/>
					</div>
					{
						errorText && (<div className="stripe-card-registration__error">
							<Error text={errorText}/>
						</div>)
					}
				</div>
			</ErrorBoundary>
		</DrawerPopup>
	</Elements>)
}
